/** Small shared presentational bits: the Sentiq wordmark glyph, status pills, and a busy button. */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { EnrolToken, Gateway } from './api';

export function Wordmark({ sub = 'Command' }: { sub?: string }): JSX.Element {
  return (
    <div className="wordmark">
      <svg className="glyph" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="3.4" fill="currentColor" />
        <path
          d="M10.3 21.7a8 8 0 0 1 0-11.4M21.7 10.3a8 8 0 0 1 0 11.4M6.8 25.2a13 13 0 0 1 0-18.4M25.2 6.8a13 13 0 0 1 0 18.4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
      <div>
        <div className="name">
          SENT<em>IQ</em>
        </div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

export function Button({
  busy,
  children,
  className = 'btn btn-primary',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }): JSX.Element {
  return (
    <button className={className} disabled={busy || rest.disabled} {...rest}>
      {busy ? '…' : children}
    </button>
  );
}

/** Enrolment + link state for a gateway, as a coloured pill. */
export function GatewayState({ gw }: { gw: Gateway }): JSX.Element {
  if (!gw.enrolled)
    return (
      <span className="pill warn">
        <span className="dot" />
        Not enrolled
      </span>
    );
  if (gw.online)
    return (
      <span className="pill good">
        <span className="dot" />
        Online
      </span>
    );
  return (
    <span className="pill idle">
      <span className="dot" />
      Offline
    </span>
  );
}

export function Banner({
  kind,
  children,
}: {
  kind: 'err' | 'ok' | 'warn';
  children: ReactNode;
}): JSX.Element {
  return <div className={`banner ${kind}`}>{children}</div>;
}

/**
 * The router's enrolment token, shown once. The line it prints is the whole of the router-side
 * commissioning apart from the WebUI-only bits (failover, GNSS): it generates the key and the
 * platform account on the router, claims the tunnel, and writes the site posture.
 */
export function EnrolTokenCard({
  token,
  onDismiss,
}: {
  token: EnrolToken;
  onDismiss: () => void;
}): JSX.Element {
  const expires = new Date(token.expires_at);
  return (
    <div className="panel panel-pad" style={{ marginBottom: 16, borderColor: 'var(--amber-deep)' }}>
      <div className="section-label" style={{ color: 'var(--amber)' }}>
        Router enrolment token — {token.site_code}
      </div>
      <Banner kind="ok">
        Shown <b>only once</b>. Run this on the RUT956 (with <code>enrol.sh</code> and{' '}
        <code>provision-sentiq-user.sh</code> copied to it); the router does the rest. Valid until{' '}
        {expires.toLocaleString()} — mint another from the site page if it lapses.
      </Banner>
      <pre
        className="mono"
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13, margin: '8px 0' }}
      >
        {token.command}
      </pre>
      <div className="form-actions">
        <button className="btn btn-ghost" onClick={onDismiss}>
          Dismiss
        </button>
        <button
          className="btn btn-primary"
          onClick={() => void navigator.clipboard?.writeText(token.command)}
        >
          Copy command
        </button>
      </div>
    </div>
  );
}

/** How old a timestamp is, as people say it. */
function ago(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
const recent = (iso: string | null, seconds: number): boolean =>
  !!iso && Date.now() - new Date(iso).getTime() < seconds * 1000;

/**
 * The commissioning chain for one gateway — the runbook's phase 8 as a row of steps that go green
 * in order. It answers, without a shell, the questions that used to need psql on the droplet:
 * has the router claimed its tunnel, is its peer on the hub, is the controller answering polls.
 *
 * Each step is a timestamp with a freshness rule, not a boolean, because the interesting states are
 * the stale ones: a hub peer confirmed 20 minutes ago on a fleet that syncs every 30 s means the
 * sweep has stopped; a poll 10 minutes old with the peer fresh means the site is dialling but the
 * controller is not answering.
 */
export function CommissioningChain({ gw }: { gw: Gateway }): JSX.Element {
  type Step = { label: string; state: 'done' | 'pending' | 'stale' | 'fail'; detail: string };
  const cloud = gw.edge_mode !== 'site';
  const steps: Step[] = [
    {
      label: 'Certificate',
      state: gw.enrolled ? 'done' : 'pending',
      detail: gw.enrolled
        ? `issued, expires ${gw.cert_expires_at ? new Date(gw.cert_expires_at).toLocaleDateString() : '—'}`
        : 'not issued',
    },
    {
      label: 'Router enrolled',
      // enrolled_at is stamped by the token flow. A site with a tunnel address but no stamp was
      // commissioned by hand before that existed — enrolled, just not by enrol.sh.
      state: gw.enrolled_at || gw.wg_address ? 'done' : gw.token_live ? 'pending' : 'fail',
      detail: gw.enrolled_at
        ? `${ago(gw.enrolled_at)} · ${gw.wg_address ?? ''}${gw.hw_serial ? ` · s/n ${gw.hw_serial}` : ''}`
        : gw.wg_address
          ? `${gw.wg_address} · by hand, before tokens`
          : gw.token_live
            ? `token live until ${gw.enrol_token_expires_at ? new Date(gw.enrol_token_expires_at).toLocaleString() : '—'} — run enrol.sh on the router`
            : 'no live token — mint one',
    },
    {
      label: 'Hub peer',
      state: !gw.wg_address
        ? 'pending'
        : recent(gw.hub_peer_synced_at, 120)
          ? 'done'
          : gw.hub_peer_synced_at
            ? 'stale'
            : 'pending',
      detail: !gw.wg_address
        ? 'after enrolment'
        : gw.hub_peer_synced_at
          ? `confirmed ${ago(gw.hub_peer_synced_at)}${recent(gw.hub_peer_synced_at, 120) ? '' : ' — sync sweep not running?'}`
          : 'waiting for the supervisor sweep (≤ 30 s)',
    },
    {
      label: cloud ? 'Controller polling' : 'Site edge',
      // last_seen_at is bumped by ingest on every telemetry row, so `online` (the server's 180 s
      // rule on it) is the poll freshness. edge_status is the supervisor's verdict on the agent.
      state:
        gw.edge_status === 'running' && gw.online
          ? 'done'
          : gw.edge_status === 'unreachable' || gw.edge_status === 'backoff'
            ? 'fail'
            : gw.last_seen_at
              ? 'stale'
              : 'pending',
      detail:
        gw.edge_status === 'running' && gw.online
          ? `live, last poll ${ago(gw.last_seen_at)}`
          : `${gw.edge_status}${gw.last_seen_at ? `, last poll ${ago(gw.last_seen_at)}` : ', no poll yet'}${gw.edge_last_error ? ` — ${gw.edge_last_error}` : ''}`,
    },
  ];
  const glyph = { done: '✓', pending: '·', stale: '!', fail: '✕' } as const;
  return (
    <ol className="chain" aria-label="commissioning">
      {steps.map((s) => (
        <li className={`chain-step ${s.state}`} key={s.label} title={s.detail}>
          <span className="chain-glyph">{glyph[s.state]}</span>
          <span className="chain-label">{s.label}</span>
          <span className="chain-detail">{s.detail}</span>
        </li>
      ))}
    </ol>
  );
}
